/**
 * Shared scroll-animation runtime source for preview, export, and saved preview
 * documents. The returned string is a self-contained classic script.
 *
 * The reveal-convention table is serialized in from the motion registry
 * (`scrollRevealRules()` — the same derivation the parser bakes from), so the
 * classes the parser pre-applies and the classes the runtime re-earns at scroll
 * time cannot drift apart. Per convention:
 *   add    — the parser's baked revealed state
 *   init   — the subset of `add` that only marks "runtime hooked"; never stripped
 *   remove — offscreen classes the parser removed; re-armed below the fold
 *
 * Above-the-fold failsafe (M6): an element is only re-armed (end classes
 * stripped, offscreen classes restored) when it starts BELOW the first
 * viewport. Anything above the fold, inside a display:none drawer, or
 * otherwise unlaid-out keeps the parser's baked revealed state — so an
 * observer that never fires can never strand content invisible, and above-fold
 * AOS elements no longer flash out and back in on load.
 *
 * Motion reconstruction drivers (M7), keyed on the stamps the parser writes:
 *   parallax-var — writes the host's drive variable WITH px units (the
 *     captured stylesheet rule multiplies a unitless var and was dead as
 *     written); sign of data-offset preserved, only magnitude clamped, layers
 *     scale by their authored --offset.
 *   marquee — rAF translate with seamless wrap at the track's repetition
 *     period; tracks matching the registry's reverseMarkers run the other way.
 *   word-rotator (IF-4) — cycles the wrapper's `data-words` list. Its rest
 *     state is INVISIBLE by the captured stylesheet (letters at opacity 0 /
 *     rotateX(-90deg), the word out of flow), so ownership is explicit: the
 *     driver writes a visible current word inline and only THEN stamps the
 *     armed attribute that lets the collapse CSS step aside. Inline styles
 *     outrank that CSS, so a driver that dies mid-cycle leaves a readable word
 *     rather than an empty box — which is the guarantee the M6 fold check
 *     provides for reveal conventions, obtained here by construction instead.
 * All three are reconstructions tuned to look right, not recovered site code,
 * and all three pause under prefers-reduced-motion / automation / reorder.
 */

import { scrollRevealRules } from './motion-residue.js';
import { MOTION_PATTERNS } from './motion-patterns.js';
import { WORD_ROTATOR_RUNTIME_CONTRACT } from './motion-static-css.js';

function revealConventionTable() {
  return scrollRevealRules().map((rule) => ({
    selector: rule.selector,
    add: rule.add,
    remove: rule.remove,
    init: rule.init || [],
    // The one bit that separates a convention the BUILDER stamps from one a
    // crawl captured. The mode operations act on builder rows; the template
    // toggle acts on the others. Projected from the registry's `owner` so the
    // runtime never carries its own list of which is which.
    pb: rule.owner === 'builder'
  }));
}

function stampSelectorOf(pattern) {
  const stamp = pattern?.stamp;
  if (!stamp?.name || !stamp?.value) return null;
  return `[${stamp.name}="${stamp.value}"]`;
}

/** Driver config serialized into the runtime — derived from the registry. */
function motionDriverConfig() {
  const config = { parallax: null, marquee: null, wordRotator: null };
  for (const pattern of MOTION_PATTERNS) {
    if (pattern.staticFallback?.kind === 'word-rotator' && stampSelectorOf(pattern)) {
      // Every selector and attribute comes from the registry contract; the
      // runtime never restates one.
      config.wordRotator = { ...WORD_ROTATOR_RUNTIME_CONTRACT };
    }
    if (pattern.id === 'parallax-var') {
      const selector = stampSelectorOf(pattern);
      if (selector && pattern.driveVar && pattern.amplitudeAttr) {
        config.parallax = {
          selector,
          driveVar: pattern.driveVar,
          amplitudeAttr: pattern.amplitudeAttr
        };
      }
    }
    if (pattern.id === 'marquee') {
      const selector = stampSelectorOf(pattern);
      if (selector) {
        config.marquee = {
          selector,
          reverse: (pattern.reverseMarkers || []).join(', ') || null
        };
      }
    }
  }
  return config;
}

const SCROLL_ANIMATION_RUNTIME_SOURCE = String.raw`(function() {
  'use strict';

  // Handshake, set before anything else — in particular before any baked
  // reveal class is stripped. M6's fallback hiding CSS is gated on
  // html[data-pb-motion-runtime], so content can only be hidden by a runtime
  // that is present and will reveal it: no script executed, no hiding.
  try {
    if (document.documentElement) {
      document.documentElement.setAttribute('data-pb-motion-runtime', '');
    }
  } catch (err) {}

  var REVEAL_CONVENTIONS = "__PB_REVEAL_CONVENTIONS__";
  var MOTION_DRIVERS = "__PB_MOTION_DRIVERS__";

  var observedReveal = typeof WeakSet === 'function' ? new WeakSet() : null;
  var revealObserver = null;

  // ── entrance mode state (EA-2) ────────────────────────────────────────
  //
  // Both are read from the DOCUMENT before anything can arm, not from a
  // message. This runtime is appended LAST into the editor's bridge script and
  // auto-inits at parse time, so a mode message from the parent frame cannot
  // reliably arrive first — suppression has to be a property of the document
  // the renderer built. Generated documents never carry the canvas marker.
  var ENTRANCE_ARMED_CLASS = 'pb-reveal-armed';
  // 0.6s transition + up to a 0.6s stagger delay, plus margin. The armed class
  // must not outlive its transition; a missed transitionend must not strand it.
  var ARMED_FALLBACK_MS = 1600;
  var entranceSuppressed = false;
  var templateEntrancesEnabled = true;
  var armedTimers = typeof WeakMap === 'function' ? new WeakMap() : null;

  try {
    var pbDocEl = document.documentElement;
    if (pbDocEl) {
      if (pbDocEl.hasAttribute('data-pb-entrance-canvas')) entranceSuppressed = true;
      if (pbDocEl.getAttribute('data-pb-entrance-template') === 'off') {
        templateEntrancesEnabled = false;
      }
    }
  } catch (err) {}

  var parallaxBound = false;
  var parallaxFrame = 0;
  var translateBaseTransforms = typeof WeakMap === 'function' ? new WeakMap() : null;

  function shouldRevealImmediately() {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return true;
      }
    } catch (err) {}
    try {
      if (navigator && navigator.webdriver === true) return true;
    } catch (err) {}
    try {
      var search = String(window.location && window.location.search || '');
      if (search) {
        var params = new URLSearchParams(search);
        if (params.get('screenshot') === '1') return true;
        if (params.get('reveal') === 'all') return true;
      }
    } catch (err) {
      var fallbackSearch = String(window.location && window.location.search || '');
      if (/[?&]screenshot=1(?:&|$)/.test(fallbackSearch)) return true;
      if (/[?&]reveal=all(?:&|$)/.test(fallbackSearch)) return true;
    }
    return false;
  }

  function addClasses(el, names) {
    for (var i = 0; i < names.length; i++) el.classList.add(names[i]);
  }

  function removeClasses(el, names) {
    for (var i = 0; i < names.length; i++) el.classList.remove(names[i]);
  }

  function endClassesOf(convention) {
    var out = [];
    for (var i = 0; i < convention.add.length; i++) {
      if (convention.init.indexOf(convention.add[i]) === -1) out.push(convention.add[i]);
    }
    return out;
  }

  /** The parser's baked revealed state: init + end classes on, offscreen off. */
  function applyRevealedState(el, convention) {
    addClasses(el, convention.add);
    removeClasses(el, convention.remove);
  }

  function nodesFor(convention) {
    try {
      return document.querySelectorAll(convention.selector);
    } catch (err) {
      return [];
    }
  }

  function unobserveReveal(el) {
    if (!revealObserver) return;
    try { revealObserver.unobserve(el); } catch (err) {}
  }

  /** A convention the current mode has switched off arms nothing new. */
  function conventionEnabled(convention) {
    return convention.pb ? !entranceSuppressed : templateEntrancesEnabled;
  }

  function clearArmedTimer(el) {
    if (!armedTimers) return;
    var timer = armedTimers.get(el);
    if (timer) {
      clearTimeout(timer);
      armedTimers['delete'](el);
    }
  }

  function armEntrance(el) {
    clearArmedTimer(el);
    if (el.classList) el.classList.add(ENTRANCE_ARMED_CLASS);
  }

  function disarmEntrance(el) {
    clearArmedTimer(el);
    if (el.classList) el.classList.remove(ENTRANCE_ARMED_CLASS);
  }

  /**
   * The armed class is EPHEMERAL, and that is the whole reason entrance CSS can
   * carry a "transition" declaration at all: while it is present it replaces whatever
   * transition the template authored on that element, so a card whose hover
   * effect lives on the same property would stop working if it stayed. It comes
   * off the moment the entrance finishes. transitionend is not guaranteed — a
   * cancelled transition, a node that never painted, an ancestor that went
   * display:none — so a bounded timer is the backstop, never the primary path.
   */
  function disarmWhenSettled(el) {
    if (!el.addEventListener) {
      disarmEntrance(el);
      return;
    }
    var settle = function() {
      el.removeEventListener('transitionend', settle);
      el.removeEventListener('transitioncancel', settle);
      disarmEntrance(el);
    };
    el.addEventListener('transitionend', settle);
    el.addEventListener('transitioncancel', settle);
    if (armedTimers) {
      clearArmedTimer(el);
      armedTimers.set(el, setTimeout(settle, ARMED_FALLBACK_MS));
    }
  }

  function revealAll() {
    for (var i = 0; i < REVEAL_CONVENTIONS.length; i++) {
      var convention = REVEAL_CONVENTIONS[i];
      // A template convention the project switched off is not re-touched. Its
      // nodes were revealed and unobserved when it was disabled, and applying
      // the end classes again would edit markup the user asked us to leave
      // alone — the difference between "not animated" and "animated instantly".
      if (!convention.pb && !templateEntrancesEnabled) continue;
      var nodes = nodesFor(convention);
      for (var j = 0; j < nodes.length; j++) {
        applyRevealedState(nodes[j], convention);
        // Immediate, not on transitionend: this path exists for reduced motion,
        // ?reveal=all and no-IntersectionObserver, none of which should leave a
        // transition declaration behind on the element.
        if (convention.pb) disarmEntrance(nodes[j]);
      }
    }
  }

  /**
   * The above-the-fold failsafe. Only an element that starts below the first
   * viewport may be re-armed; a zero-rect element (display:none drawer,
   * unlaid-out subtree) reports top 0 and is deliberately left revealed.
   */
  function belowViewport(el) {
    if (!el.getBoundingClientRect) return false;
    var rect = el.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.top >= viewportHeight;
  }

  function revealElement(el) {
    for (var i = 0; i < REVEAL_CONVENTIONS.length; i++) {
      var convention = REVEAL_CONVENTIONS[i];
      try {
        if (!el.matches || !el.matches(convention.selector)) continue;
        applyRevealedState(el, convention);
        if (convention.pb) disarmWhenSettled(el);
      } catch (err) {}
    }
  }

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    if (typeof IntersectionObserver !== 'function') return null;
    revealObserver = new IntersectionObserver(function(entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry || (!entry.isIntersecting && !(entry.intersectionRatio > 0))) continue;
        var target = entry.target;
        if (!target || !target.classList) continue;
        revealElement(target);
        try { revealObserver.unobserve(target); } catch (err) {}
      }
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.01
    });
    return revealObserver;
  }

  function isObservedReveal(el) {
    return observedReveal
      ? observedReveal.has(el)
      : el && el.getAttribute('data-pb-motion-observed') === '1';
  }

  function markObservedReveal(el) {
    if (observedReveal) {
      observedReveal.add(el);
      return;
    }
    if (el && el.setAttribute) el.setAttribute('data-pb-motion-observed', '1');
  }

  function clearObservedReveal(el) {
    if (observedReveal) {
      observedReveal['delete'](el);
      return;
    }
    if (el && el.removeAttribute) el.removeAttribute('data-pb-motion-observed');
  }

  /**
   * Once-only discovery, preserved for EVERY convention including builder rows.
   * Unrelated re-inits — an SVG swap, an image swap, an editable HTML write, a
   * section replace — must discover NEW nodes and leave armed or completed ones
   * exactly as they are. Re-arming on a re-init would make a stagger replay
   * every time the user edited a heading.
   *
   * @param {function|null} filter — restricts the pass to some conventions.
   *   Used by replayBuilderEntrances so a replay cannot disturb template rows.
   */
  function hookRevealElements(filter) {
    var observer = getRevealObserver();
    if (!observer) {
      revealAll();
      return;
    }
    for (var i = 0; i < REVEAL_CONVENTIONS.length; i++) {
      var convention = REVEAL_CONVENTIONS[i];
      if (filter && !filter(convention)) continue;
      var nodes = nodesFor(convention);
      var endClasses = endClassesOf(convention);
      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];
        addClasses(node, convention.init);
        if (isObservedReveal(node)) continue;
        markObservedReveal(node);
        // A switched-off convention is DISCOVERED but never armed: the node
        // keeps the baked revealed state and simply does not animate. Marking
        // it observed is deliberate — turning the convention back on goes
        // through an explicit replay, never through an incidental re-init.
        if (!conventionEnabled(convention)) continue;
        if (!belowViewport(node)) continue;
        removeClasses(node, endClasses);
        addClasses(node, convention.remove);
        if (convention.pb) armEntrance(node);
        try { observer.observe(node); } catch (err) {}
      }
    }
  }

  function isBuilderConvention(convention) {
    return !!convention.pb;
  }

  /**
   * Force every builder-row match visible and KEEP it visible (true), or make
   * builder rows armable again for below-viewport nodes (false).
   *
   * Clear-first and self-healing: whatever state a node is in — armed,
   * mid-transition, freshly stamped by a re-render — it ends revealed,
   * disarmed and unobserved. The editing canvas holds this on permanently;
   * entering the real Preview releases it and then replays.
   */
  function setEntranceSuppressed(suppressed) {
    entranceSuppressed = !!suppressed;
    if (!entranceSuppressed) return;
    for (var i = 0; i < REVEAL_CONVENTIONS.length; i++) {
      var convention = REVEAL_CONVENTIONS[i];
      if (!isBuilderConvention(convention)) continue;
      var nodes = nodesFor(convention);
      for (var j = 0; j < nodes.length; j++) {
        disarmEntrance(nodes[j]);
        applyRevealedState(nodes[j], convention);
        unobserveReveal(nodes[j]);
      }
    }
  }

  /**
   * Re-arm builder entrances from scratch. Called ONLY on a real Preview enter
   * (and an explicit re-enter) — never from a plain re-init, which is what
   * keeps an unrelated edit from replaying the whole page.
   */
  function replayBuilderEntrances() {
    if (entranceSuppressed) return;
    if (shouldRevealImmediately() || typeof IntersectionObserver !== 'function') {
      revealAll();
      return;
    }
    for (var i = 0; i < REVEAL_CONVENTIONS.length; i++) {
      var convention = REVEAL_CONVENTIONS[i];
      if (!isBuilderConvention(convention)) continue;
      var nodes = nodesFor(convention);
      for (var j = 0; j < nodes.length; j++) {
        clearObservedReveal(nodes[j]);
        disarmEntrance(nodes[j]);
        unobserveReveal(nodes[j]);
      }
    }
    hookRevealElements(isBuilderConvention);
  }

  /**
   * The project's "keep the template's own scroll reveal" switch. Turning it
   * off must not strand a node the observer already armed: those are revealed
   * and unobserved here, because nothing is coming to reveal them afterwards.
   */
  function setTemplateEntrancesEnabled(enabled) {
    templateEntrancesEnabled = enabled !== false;
    if (templateEntrancesEnabled) return;
    for (var i = 0; i < REVEAL_CONVENTIONS.length; i++) {
      var convention = REVEAL_CONVENTIONS[i];
      if (isBuilderConvention(convention)) continue;
      var nodes = nodesFor(convention);
      for (var j = 0; j < nodes.length; j++) {
        applyRevealedState(nodes[j], convention);
        unobserveReveal(nodes[j]);
      }
    }
  }

  function transformHasTranslateY(transform) {
    return /translateY\(\s*[-+]?[\d.]+(?:px|rem|em|vh|vw|%)?\s*\)/i.test(String(transform || ''));
  }

  function getBaseTransform(el) {
    if (!el) return '';
    if (translateBaseTransforms && translateBaseTransforms.has(el)) {
      return translateBaseTransforms.get(el);
    }
    var transform = el.style && typeof el.style.transform === 'string'
      ? el.style.transform
      : '';
    if (!transformHasTranslateY(transform)) return '';
    if (translateBaseTransforms) translateBaseTransforms.set(el, transform);
    return transform;
  }

  function replaceTranslateY(transform, valuePx) {
    return String(transform || '').replace(
      /translateY\(\s*[-+]?[\d.]+(?:px|rem|em|vh|vw|%)?\s*\)/i,
      'translateY(' + valuePx.toFixed(2) + 'px)'
    );
  }

  function reorderActive() {
    return !!(document.documentElement && document.documentElement.hasAttribute('data-pb-reorder-active'));
  }

  function updateTranslateParallax() {
    var nodes = document.querySelectorAll('.translate');
    if (!nodes.length) return;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
    var viewportCenter = viewportHeight / 2;
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var baseTransform = getBaseTransform(node);
      if (!baseTransform) continue;
      var rect = node.getBoundingClientRect ? node.getBoundingClientRect() : null;
      if (!rect) continue;
      var elementCenter = rect.top + rect.height / 2;
      var delta = (viewportCenter - elementCenter) * 0.08;
      if (delta > 80) delta = 80;
      if (delta < -80) delta = -80;
      node.style.transform = replaceTranslateY(baseTransform, delta);
    }
  }

  // The captured site wrote --scrollPos values up to ~305px; the clamp keeps a
  // runaway data-offset (corpus max magnitude 800) inside that observed range.
  var PARALLAX_MAX_AMPLITUDE = 320;

  function updateParallaxVar() {
    var config = MOTION_DRIVERS.parallax;
    if (!config) return;
    var hosts = document.querySelectorAll(config.selector);
    if (!hosts.length) return;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
    var viewportCenter = viewportHeight / 2;
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var rect = host.getBoundingClientRect ? host.getBoundingClientRect() : null;
      if (!rect) continue;
      var hostCenter = rect.top + rect.height / 2;
      var progress = (viewportCenter - hostCenter) / viewportHeight;
      if (progress > 1) progress = 1;
      if (progress < -1) progress = -1;
      // Sign of data-offset is preserved and only the magnitude is clamped —
      // the corpus is mixed-sign, and Math.abs() would reverse the drift on
      // most hosts. Layers scale the px value by their authored --offset.
      var raw = parseFloat(host.getAttribute(config.amplitudeAttr));
      var magnitude = Math.min(Math.abs(raw) || 80, PARALLAX_MAX_AMPLITUDE);
      var amplitude = raw < 0 ? -magnitude : magnitude;
      host.style.setProperty(config.driveVar, (progress * amplitude).toFixed(2) + 'px');
    }
  }

  function updateScrollDrivers() {
    parallaxFrame = 0;
    if (shouldRevealImmediately() || reorderActive()) return;
    updateTranslateParallax();
    updateParallaxVar();
  }

  function scheduleParallax() {
    if (parallaxFrame) return;
    parallaxFrame = requestAnimationFrame(updateScrollDrivers);
  }

  function setupScrollDrivers() {
    if (shouldRevealImmediately()) return;
    var hasTranslate = !!document.querySelector('.translate');
    var hasParallaxVar = !!(
      MOTION_DRIVERS.parallax && document.querySelector(MOTION_DRIVERS.parallax.selector)
    );
    if (!hasTranslate && !hasParallaxVar) return;
    if (!parallaxBound) {
      window.addEventListener('scroll', scheduleParallax, { passive: true });
      window.addEventListener('resize', scheduleParallax, { passive: true });
      parallaxBound = true;
    }
    scheduleParallax();
  }

  var marqueeStates = typeof WeakMap === 'function' ? new WeakMap() : null;
  var marqueeFrame = 0;
  var marqueeLastTimestamp = 0;
  var MARQUEE_SPEED = 0.03; // px per ms (~30px/s), timestamp-scaled

  /**
   * A content signature stable across editor attribute stamping: tag, image
   * sources, trimmed text. Used to find the track's repetition period so the
   * wrap point is seamless — neudigital ships each logo set tripled.
   */
  function marqueeItemSignature(el) {
    var parts = [el.tagName || ''];
    var imgs = el.querySelectorAll ? el.querySelectorAll('img') : [];
    for (var i = 0; i < imgs.length; i++) parts.push(imgs[i].getAttribute('src') || '');
    parts.push(String(el.textContent || '').trim());
    return parts.join('|');
  }

  /** Fraction of the track width that makes one seamless period (1 = no repeat). */
  function marqueePeriodRatio(track) {
    var children = track.children || [];
    var count = children.length;
    if (!count) return 1;
    var signatures = [];
    for (var i = 0; i < count; i++) signatures.push(marqueeItemSignature(children[i]));
    for (var period = 1; period <= count / 2; period++) {
      if (count % period !== 0) continue;
      var repeats = true;
      for (var j = period; j < count; j++) {
        if (signatures[j] !== signatures[j % period]) {
          repeats = false;
          break;
        }
      }
      if (repeats) return period / count;
    }
    return 1;
  }

  function marqueeTick(timestamp) {
    marqueeFrame = 0;
    var config = MOTION_DRIVERS.marquee;
    if (!config) return;
    var tracks = document.querySelectorAll(config.selector);
    if (!tracks.length) return;

    var delta = marqueeLastTimestamp ? timestamp - marqueeLastTimestamp : 16;
    if (delta > 64) delta = 64;
    if (delta < 0) delta = 0;
    marqueeLastTimestamp = timestamp;

    if (!shouldRevealImmediately() && !reorderActive()) {
      for (var i = 0; i < tracks.length; i++) {
        var track = tracks[i];
        var state = marqueeStates ? marqueeStates.get(track) : null;
        if (!state) {
          state = { offset: 0, ratio: marqueePeriodRatio(track) };
          if (marqueeStates) marqueeStates.set(track, state);
        }
        // scrollWidth is re-read every tick so late-loading images grow the
        // wrap distance instead of leaving a stale, too-early wrap point.
        var wrap = (track.scrollWidth || 0) * state.ratio;
        if (!wrap) continue;
        var direction = config.reverse && track.matches && track.matches(config.reverse) ? 1 : -1;
        var raw = state.offset + direction * MARQUEE_SPEED * delta;
        // Normalize into (-wrap, 0] for either direction, so the visible
        // content always covers the viewport and the wrap is invisible.
        state.offset = -((((-raw) % wrap) + wrap) % wrap);
        track.style.transform = 'translate3d(' + state.offset.toFixed(2) + 'px, 0, 0)';
      }
    }

    marqueeFrame = requestAnimationFrame(marqueeTick);
  }

  function setupMarquee() {
    if (shouldRevealImmediately()) return;
    var config = MOTION_DRIVERS.marquee;
    if (!config) return;
    if (!document.querySelector(config.selector)) return;
    if (marqueeFrame) return;
    marqueeLastTimestamp = 0;
    marqueeFrame = requestAnimationFrame(marqueeTick);
  }

  // ── word rotator (IF-4) ────────────────────────────────────────────────
  //
  // Ownership, not decoration. Unarmed, the collapse CSS holds the captured
  // word visible at the captured wrapper width. This driver writes a visible
  // word INLINE and only then stamps the armed attribute, so at no point is
  // there a frame where the captured stylesheet's invisible rest state applies
  // without an inline override on top of it. Everything it reads about the
  // markup comes from MOTION_DRIVERS.wordRotator, which is the registry entry.

  var ROTATOR_MAX_WORDS = 24;
  var ROTATOR_MAX_WORD_LENGTH = 64;
  var ROTATOR_HOLD_MS = 2000;
  var ROTATOR_TRANSITION_MS = 400;
  var ROTATOR_STAGGER_MS = 35;
  // States, not a map keyed by element: the array IS the live set, so pruning
  // a disconnected host cannot leave a stale entry behind somewhere else.
  var rotatorStates = [];
  var rotatorTimer = 0;
  var rotatorBound = false;

  /**
   * data-words is authored content, so it is validated rather than trusted:
   * a JSON array of 2..N non-empty strings, each bounded in length. Anything
   * else leaves the element unarmed, which means the captured static word
   * keeps rendering — the failure mode is "no animation", never "no text".
   */
  function parseRotatorWords(raw) {
    var parsed;
    try {
      parsed = JSON.parse(String(raw == null ? '' : raw));
    } catch (err) {
      return null;
    }
    if (Object.prototype.toString.call(parsed) !== '[object Array]') return null;
    if (parsed.length < 2 || parsed.length > ROTATOR_MAX_WORDS) return null;
    var words = [];
    for (var i = 0; i < parsed.length; i++) {
      if (typeof parsed[i] !== 'string') return null;
      var word = parsed[i].trim();
      if (!word || word.length > ROTATOR_MAX_WORD_LENGTH) return null;
      words.push(word);
    }
    return words;
  }

  function capitalizeFirst(word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  /**
   * The captured frame is the evidence for how the site's own script rendered
   * a list entry: tcvisuals shipped "Creative" for the entry "creative". The
   * casing transform is DERIVED from that comparison rather than assumed, so a
   * site that renders its list verbatim keeps rendering it verbatim.
   */
  function rotatorCasing(words, capturedWord) {
    var captured = String(capturedWord || '').trim();
    for (var i = 0; i < words.length; i++) {
      if (words[i] === captured) return { capitalize: false, index: i };
      if (capitalizeFirst(words[i]) === captured) return { capitalize: true, index: i };
    }
    return { capitalize: false, index: 0 };
  }

  function rotatorLetters(state) {
    return state.word && state.word.children ? state.word.children : [];
  }

  function writeRotatorLetters(state, text) {
    while (state.word.firstChild) state.word.removeChild(state.word.firstChild);
    for (var i = 0; i < text.length; i++) {
      var span = document.createElement('span');
      // textContent only: a word list is authored data and must never become
      // markup. A literal space in an inline-block letter box collapses to
      // nothing, so a two-word entry would render as one — NBSP keeps the gap.
      var ch = text.charAt(i);
      span.textContent = ch === ' ' ? '\u00a0' : ch;
      span.style.opacity = '0';
      span.style.transform = 'rotateX(-90deg)';
      state.word.appendChild(span);
    }
  }

  function setRotatorLettersVisible(state, visible, animate) {
    var letters = rotatorLetters(state);
    for (var i = 0; i < letters.length; i++) {
      var letter = letters[i];
      var order = visible ? i : letters.length - 1 - i;
      letter.style.transitionDelay = animate ? order * ROTATOR_STAGGER_MS + 'ms' : '0ms';
      letter.style.opacity = visible ? '1' : '0';
      letter.style.transform = visible ? 'rotateX(0deg)' : 'rotateX(-90deg)';
    }
  }

  function rotatorWordWidth(state) {
    if (!state.word || !state.word.getBoundingClientRect) return 0;
    return state.word.getBoundingClientRect().width;
  }

  function setRotatorWidth(state, px) {
    if (!(px > 0)) return;
    state.host.style.width = px + 'px';
  }

  /**
   * Take ownership of one wrapper. Returns false — leaving the element to the
   * collapse CSS — for anything it cannot fully drive.
   */
  function armRotator(host, config) {
    if (host.hasAttribute(config.armedAttr)) return false;
    var word = host.querySelector(config.wordSuffix.trim());
    if (!word) return false;
    var captured = String(word.textContent || '').trim();
    var words = parseRotatorWords(host.getAttribute(config.wordsAttr));
    if (!words || !captured) return false;
    // No per-letter boxes means this is not the shape the collapse CSS and the
    // stagger assume. Leave it to the fallback rather than rebuild markup the
    // crawl never had.
    if (!word.children || word.children.length === 0) return false;

    var casing = rotatorCasing(words, captured);
    var state = {
      host: host,
      word: word,
      words: words,
      capitalize: casing.capitalize,
      index: casing.index,
      phase: 0,
      swapTimer: 0
    };

    // The width the collapse CSS is holding right now IS the site's own
    // measurement. Freeze it inline before arming so the wrapper never
    // collapses to zero in the frame between the two.
    var held = host.getBoundingClientRect ? host.getBoundingClientRect().width : 0;
    setRotatorWidth(state, held);

    // The animated letters are decoration for a screen reader; a live region
    // cycling four words would be read as four interruptions. The captured
    // word stays in the accessibility tree as ordinary text.
    var reader = document.createElement('span');
    reader.setAttribute('data-pb-rotator-label', '');
    reader.textContent = captured;
    reader.style.cssText =
      'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;' +
      'clip-path:inset(50%);white-space:nowrap;border:0';
    host.insertBefore(reader, host.firstChild);
    host.setAttribute('aria-live', 'off');
    word.setAttribute('aria-hidden', 'true');

    // Visible state written INLINE first; the armed stamp second. Reversing
    // these two lines is the whole bug this ordering exists to prevent.
    setRotatorLettersVisible(state, true, false);
    host.setAttribute(config.armedAttr, '');

    rotatorStates.push(state);

    // A width measured before webfonts land is the wrong width. Remeasure once
    // they have, and never treat a missing fonts API as a reason to skip it.
    try {
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(function() {
          if (!host.isConnected) return;
          setRotatorWidth(state, rotatorWordWidth(state));
        }).catch(function() {});
      }
    } catch (err) {}
    return true;
  }

  function rotatorPaused() {
    if (shouldRevealImmediately() || reorderActive()) return true;
    return !!(document.hidden || document.visibilityState === 'hidden');
  }

  function advanceRotator(state) {
    if (state.swapTimer) return; // one loop per element, ever
    setRotatorLettersVisible(state, false, true);
    var outMs = ROTATOR_TRANSITION_MS + rotatorLetters(state).length * ROTATOR_STAGGER_MS;
    state.swapTimer = setTimeout(function() {
      state.swapTimer = 0;
      if (!state.host.isConnected) return;
      state.index = (state.index + 1) % state.words.length;
      var next = state.words[state.index];
      writeRotatorLetters(state, state.capitalize ? capitalizeFirst(next) : next);
      // Letters exist at opacity 0 but still occupy their inline-block boxes,
      // so the new width is measurable before anything fades in.
      setRotatorWidth(state, rotatorWordWidth(state));
      setRotatorLettersVisible(state, true, true);
    }, outMs);
  }

  function rotatorTick() {
    var live = [];
    for (var i = 0; i < rotatorStates.length; i++) {
      if (rotatorStates[i].host.isConnected) live.push(rotatorStates[i]);
      else if (rotatorStates[i].swapTimer) clearTimeout(rotatorStates[i].swapTimer);
    }
    rotatorStates = live;
    if (rotatorStates.length === 0) {
      clearInterval(rotatorTimer);
      rotatorTimer = 0;
      return;
    }
    if (rotatorPaused()) return;
    for (var j = 0; j < rotatorStates.length; j++) advanceRotator(rotatorStates[j]);
  }

  function rotatorCycleMs() {
    return ROTATOR_HOLD_MS + ROTATOR_TRANSITION_MS * 2;
  }

  function startRotatorTimer() {
    if (rotatorTimer || rotatorStates.length === 0) return;
    rotatorTimer = setInterval(rotatorTick, rotatorCycleMs());
  }

  function setupWordRotator() {
    var config = MOTION_DRIVERS.wordRotator;
    if (!config) return;
    // Reduced motion, automation, ?screenshot=1 and ?reveal=all never arm: the
    // collapse CSS already renders one static, readable word, which is exactly
    // what those modes want.
    if (shouldRevealImmediately()) return;
    var hosts;
    try {
      hosts = document.querySelectorAll(config.hostSelector);
    } catch (err) {
      return;
    }
    for (var i = 0; i < hosts.length; i++) armRotator(hosts[i], config);
    if (rotatorStates.length === 0) return;
    if (!rotatorBound) {
      rotatorBound = true;
      document.addEventListener('visibilitychange', function() {
        if (rotatorPaused()) {
          clearInterval(rotatorTimer);
          rotatorTimer = 0;
          return;
        }
        startRotatorTimer();
      });
    }
    startRotatorTimer();
  }

  /**
   * One-shot scripted preview (EA-10): disarm, strip to the hidden state,
   * force a reflow, then reveal — for EXACTLY the builder participants inside
   * scopeSelector (page scope when empty). Ends revealed, disarmed,
   * re-marked observed; the suppression FLAG is untouched — this runs even on
   * the suppressed canvas, because it is the one sanctioned animation there
   * and its end state IS the suppressed steady state. Reduced motion means
   * instant (no replay at all); a live reorder skips.
   */
  function previewEntranceOnce(scopeSelector) {
    if (shouldRevealImmediately()) return;
    if (reorderActive()) return;
    var root = document;
    if (scopeSelector) {
      try { root = document.querySelector(scopeSelector); } catch (err) { return; }
      if (!root) return;
    }
    var participants = [];
    for (var i = 0; i < REVEAL_CONVENTIONS.length; i++) {
      var convention = REVEAL_CONVENTIONS[i];
      if (!isBuilderConvention(convention)) continue;
      var nodes;
      try { nodes = root.querySelectorAll(convention.selector); } catch (err) { nodes = []; }
      for (var j = 0; j < nodes.length; j++) {
        participants.push({ el: nodes[j], convention: convention });
      }
      try {
        if (root !== document && root.matches && root.matches(convention.selector)) {
          participants.push({ el: root, convention: convention });
        }
      } catch (err) {}
    }
    // A replaced-element scope (an eid on an <img>) can be stamped on its
    // closest <picture> host. Mirror ONLY that sanctioned stamping hoist. A
    // generic closest(builder-participant) walk would replay an animated
    // ancestor when an unconfigured/nested child was explicitly requested,
    // violating the exact-target contract.
    if (!participants.length && root !== document && root.closest) {
      var scopeTag = String(root.tagName || '').toUpperCase();
      var replacedScope =
        scopeTag === 'IMG' || scopeTag === 'SVG' || scopeTag === 'VIDEO' ||
        scopeTag === 'IFRAME' || scopeTag === 'CANVAS';
      var pictureHost = null;
      if (replacedScope) {
        try { pictureHost = root.closest('picture'); } catch (err) { pictureHost = null; }
      }
      if (pictureHost && pictureHost !== root) {
        for (var c = 0; c < REVEAL_CONVENTIONS.length; c++) {
          var hostConvention = REVEAL_CONVENTIONS[c];
          if (!isBuilderConvention(hostConvention)) continue;
          var hostMatches = false;
          try { hostMatches = pictureHost.matches(hostConvention.selector); } catch (err) {}
          if (hostMatches) {
            participants.push({ el: pictureHost, convention: hostConvention });
            break;
          }
        }
      }
    }
    if (!participants.length) return;
    for (var k = 0; k < participants.length; k++) {
      var p = participants[k];
      unobserveReveal(p.el);
      clearObservedReveal(p.el);
      addClasses(p.el, p.convention.init);
      removeClasses(p.el, endClassesOf(p.convention));
      addClasses(p.el, p.convention.remove);
      armEntrance(p.el);
    }
    // Force the hidden state to actually paint before the reveal, or the
    // browser coalesces both writes into no transition at all.
    void (document.body && document.body.offsetWidth);
    var reveal = function() {
      for (var m = 0; m < participants.length; m++) {
        revealElement(participants[m].el);
        markObservedReveal(participants[m].el);
      }
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
    else setTimeout(reveal, 16);
  }

  function initScrollAnimations() {
    if (shouldRevealImmediately()) {
      revealAll();
      return;
    }
    if (typeof IntersectionObserver !== 'function') {
      revealAll();
      return;
    }
    hookRevealElements();
    setupScrollDrivers();
    setupMarquee();
    setupWordRotator();
  }

  window.__PB_INIT_SCROLL_ANIMATIONS__ = function() {
    try { initScrollAnimations(); } catch (err) {}
  };

  // Explicit mode operations for the editor bridge (EA-3). Deliberately NOT
  // folded into initScrollAnimations: a mode change is a discrete event with a
  // known cause, while a re-init happens for a dozen unrelated reasons.
  window.__PB_ENTRANCE__ = {
    setEntranceSuppressed: function(suppressed) {
      try { setEntranceSuppressed(suppressed); } catch (err) {}
    },
    replayBuilderEntrances: function() {
      try { replayBuilderEntrances(); } catch (err) {}
    },
    setTemplateEntrancesEnabled: function(enabled) {
      try { setTemplateEntrancesEnabled(enabled); } catch (err) {}
    },
    previewEntranceOnce: function(scopeSelector) {
      try { previewEntranceOnce(scopeSelector); } catch (err) {}
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.__PB_INIT_SCROLL_ANIMATIONS__, { once: true });
  } else {
    window.__PB_INIT_SCROLL_ANIMATIONS__();
  }
})();`;

let cachedRuntimeSource = null;

export function getScrollAnimationRuntimeSource() {
  if (cachedRuntimeSource === null) {
    cachedRuntimeSource = SCROLL_ANIMATION_RUNTIME_SOURCE.replace(
      '"__PB_REVEAL_CONVENTIONS__"',
      JSON.stringify(revealConventionTable())
    ).replace('"__PB_MOTION_DRIVERS__"', JSON.stringify(motionDriverConfig()));
  }
  return cachedRuntimeSource;
}
